import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'
import { getUserData } from '../utils/userData'
import { trackEvent } from '../utils/mixpanel'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const FORMAT_STYLES = {
  online: 'bg-blue-100 text-blue-800',
  'in-person': 'bg-green-100 text-green-800',
  hybrid: 'bg-purple-100 text-purple-800',
}

const FacilitatorConsultations = () => {
  const { slug } = useParams()
  const [facilitator, setFacilitator] = useState(null)
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [selectedListing, setSelectedListing] = useState(null)
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Facilitator Consultations', slug })
  }, [slug])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [profileRes, listingsRes] = await Promise.all([
          api.get(`/api/facilitators/profile/${slug}`),
          api.get(`/api/facilitators/profile/${slug}/consultations`),
        ])
        if (profileRes.data.success) setFacilitator(profileRes.data.data)
        if (listingsRes.data.success) setListings(listingsRes.data.data)
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Facilitator not found')
        } else {
          setError('Failed to load consultations')
        }
      } finally {
        setLoading(false)
      }
    }
    if (slug) fetchData()
  }, [slug])

  const openInterestModal = (listing) => {
    const userData = getUserData()
    setFormData({
      name: userData?.name || '',
      email: userData?.email || '',
      phone: userData?.phone || '',
      message: '',
    })
    setSelectedListing(listing)
    setSubmitSuccess(false)
    setSubmitError(null)
  }

  const closeModal = () => {
    setSelectedListing(null)
    setSubmitSuccess(false)
    setSubmitError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.email.trim()) return

    try {
      setSubmitting(true)
      setSubmitError(null)
      await api.post(
        `/api/facilitators/profile/${slug}/consultations/${selectedListing.id}/interest`,
        formData
      )
      trackEvent('Consultation Interest Submitted', {
        listing: selectedListing.title,
        facilitator: facilitator?.name,
      })
      setSubmitSuccess(true)
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !facilitator) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{error || 'Not found'}</h1>
          <Link to="/facilitators">
            <Button variant="primary">View All Facilitators</Button>
          </Link>
        </div>
      </div>
    )
  }

  const profilePictureUrl = facilitator.profilePicture
    ? `${API_BASE_URL}${facilitator.profilePicture}`
    : null

  return (
    <div className="bg-cream min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-b from-cream via-cream-50 to-cream pt-20 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Link
              to={`/facilitator/${slug}`}
              className="inline-flex items-center gap-3 mb-6 group"
            >
              {profilePictureUrl ? (
                <img
                  src={profilePictureUrl}
                  alt={facilitator.name}
                  className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center text-2xl text-primary-600 ring-2 ring-white shadow">
                  {facilitator.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <span className="text-lg font-semibold text-gray-700 group-hover:text-primary-600 transition-colors">
                {facilitator.name}
              </span>
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Consultations
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Express your interest in any of the consultation offerings below
              and {facilitator.name?.split(' ')[0]} will get back to you.
            </p>
          </div>
        </div>
      </div>

      {/* Listings */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {listings.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No consultations available at the moment.</p>
              <Link to={`/facilitator/${slug}`} className="text-primary-600 hover:underline mt-4 inline-block">
                Back to profile
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {listings.map((listing, index) => (
                <motion.div
                  key={listing.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow border border-gray-100"
                >
                  {listing.image && (
                    <div className="h-44 overflow-hidden bg-gray-100">
                      <img
                        src={listing.image.startsWith('http') ? listing.image : `${API_BASE_URL}${listing.image}`}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${FORMAT_STYLES[listing.format] || FORMAT_STYLES.online}`}>
                        {listing.format}
                      </span>
                      {listing.fee && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          {listing.fee.toLowerCase() === 'free' ? 'FREE' : listing.fee}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{listing.title}</h3>
                    {listing.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{listing.description}</p>
                    )}
                    {listing.duration && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {listing.duration}
                      </div>
                    )}
                    <button
                      onClick={() => openInterestModal(listing)}
                      className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
                    >
                      I&apos;m Interested
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Interest Form Modal */}
      <AnimatePresence>
        {selectedListing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Express Interest</h2>
                  <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-primary-50 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-primary-900">{selectedListing.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-2 text-sm text-primary-700">
                    {selectedListing.format && <span>{selectedListing.format}</span>}
                    {selectedListing.duration && <span>· {selectedListing.duration}</span>}
                    {selectedListing.fee && <span>· {selectedListing.fee}</span>}
                  </div>
                </div>

                {submitSuccess ? (
                  <div className="text-center py-8">
                    <div className="text-5xl mb-4">🎉</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Interest Submitted!</h3>
                    <p className="text-gray-600 mb-6">
                      {facilitator.name?.split(' ')[0]} will reach out to you soon.
                    </p>
                    <button
                      onClick={closeModal}
                      className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                      <textarea
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        placeholder="Any questions or specific needs?"
                      />
                    </div>

                    {submitError && (
                      <p className="text-red-600 text-sm">{submitError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Submitting...' : 'Submit Interest'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FacilitatorConsultations
